import { AfterViewInit, Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { NavigationEnd, NavigationExtras, Router } from '@angular/router';
import { FeatureFlagService } from 'app/services/feature-flag.service';
import { RouteMemoService } from 'app/services/route-memo.service';
import { filter as _filter } from 'lodash';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { DarkThemeSwitch } from '../dark-theme-switch';
import { LiveDataService } from '../live-data/live-data.service';
import { NoFlyTime } from '../models/NoFlyTime';
import { User } from '../models/User';
import { EmergencyService } from '../services/emergency.service';
import { HallPassesService } from '../services/hall-passes.service';
import { HttpService } from '../services/http-service';
import { KioskModeService } from '../services/kiosk-mode.service';
import { NoFlyTimeService } from '../services/no-fly-time.service';
import { ScreenService } from '../services/screen.service';
import { SideNavService } from '../services/side-nav.service';
import { StorageService } from '../services/storage.service';
import { UserService } from '../services/user.service';
import { DialogData } from '../settings/settings.component';
import { NoFlyTimeDialogComponent } from '../shared/shared-components/no-fly-time-dialog/no-fly-time-dialog.component';

declare const window;

class School {}

const DEFAULT_NAVBAR_HEIGHT = '74px';
const HIDDEN_NAVBAR_HEIGHT = '0px';

@Component({
	selector: 'app-main-page',
	templateUrl: './main-page.component.html',
	styleUrls: ['./main-page.component.scss'],
})
export class MainPageComponent implements OnInit, AfterViewInit, OnDestroy {
	toggleLeft: Observable<boolean> = new Observable<boolean>();
	sentRequests: any;
	receivedRequests: any;
	data: any;
	navbarHeight = DEFAULT_NAVBAR_HEIGHT;
	restriction$: Observable<boolean>;
	schools: School[] = [];
	isKioskMode: boolean;

	noFlyTimes: NoFlyTime[] = [];
	currentNoFlyTimeIndex: number;
	currentNoFlyEndTimeString: string;
	noFlyTimeActive = false;
	noFlyTimeTooltipDialogRef: MatDialogRef<NoFlyTimeDialogComponent>;

	private user$ = this.userService.userJSON$.pipe(
		filter((u) => !!u),
		map((user) => User.fromJSON(user))
	);
	isStaff$ = this.user$.pipe(map((user) => user.isStaff()));

	private destroy$: Subject<any> = new Subject<any>();
	emergencyActive = false;

	@HostListener('window:resize')
	checkWidth() {
		if (!this.screenService.isDeviceLargeExtra) {
			this.sideNavService.toggleLeft$.next(false);
		}

		this.navbarHeight = this.currentNavbarHeight();
	}

	constructor(
		private userService: UserService,
		public darkTheme: DarkThemeSwitch,
		public screenService: ScreenService,
		private sideNavService: SideNavService,
		private dialog: MatDialog,
		private liveDataService: LiveDataService,
		private router: Router,
		private http: HttpService,
		private passesService: HallPassesService,
		private storage: StorageService,
		private kioskModeService: KioskModeService,
		private emergencyService: EmergencyService,
		private noFlyTimeService: NoFlyTimeService,
		private featureFlagService: FeatureFlagService,
		private routeMemo: RouteMemoService
	) {
		// ##RootRouteListener
		this.routeMemo.memo('/', 'kioskMode').pipe(takeUntil(this.destroy$)).subscribe();

		this.http.schoolsCollection$
			.pipe(
				takeUntil(this.destroy$),
				map((schools) => {
					return _filter(schools, (school) => school.my_roles.length > 0);
				})
			)
			.subscribe((schools) => {
				this.schools = schools;
			});

		this.restriction$ = this.userService.blockUserPage$;

		const dbUser$ = this.http.globalReload$.pipe(
			switchMap(() => {
				return this.userService.userJSON$.pipe(
					filter((u) => !!u),
					take(1)
				);
			}),
			switchMap(() => {
				this.navbarHeight = this.currentNavbarHeight();
				return this.userService.effectiveUser$;
			})
		);

		this.http.globalReload$.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.passesService.getFiltersRequest('past-passes');
		});

		// This primes NgRx, which allows us to listen to store updates for passes, requests, etc.
		// may have to refactor its foundation
		combineLatest(
			dbUser$,
			this.passesService.passFilters$.pipe(
				filter((pass) => !!pass),
				take(1)
			)
		)
			.pipe(
				takeUntil(this.destroy$),
				tap(([user, filters]: [user: User, filters: any]) => {
					this.liveDataService.getActivePassesRequest(of({ sort: '-created', search_query: '' }), user);
					this.liveDataService.getPassLikeCollectionRequest(user);
					this.liveDataService.getExpiredPassesRequest(user, filters['past-passes'].default);
					this.liveDataService.getHallMonitorPassesRequest(of({ sort: '-created', search_query: '' }));
					if (user.roles.includes('hallpass_student')) {
						this.receivedRequests = this.liveDataService.invitations$;
						this.sentRequests = this.liveDataService.requests$;
					} else {
						this.receivedRequests = this.liveDataService.requests$;
						this.sentRequests = this.liveDataService.invitations$;
					}
					this.passesService.getPinnablesRequest();
				}),
				switchMap(([user]) => {
					return user.roles.includes('hallpass_student') ? this.liveDataService.watchActivePassLike(user) : of(null);
				})
			)
			.subscribe();
	}

	get titleColor() {
		return this.darkTheme.getColor({ dark: '#FFFFFF', white: '#1F195E' });
	}

	private currentNavbarHeight(): string {
		const { url } = this.router;
		if (url.includes('kioskMode')) {
			return DEFAULT_NAVBAR_HEIGHT;
		}

		return (url === '/main/hallmonitor' && this.screenService.isDeviceLargeExtra) || (url === '/main/myroom' && this.screenService.isDeviceLargeExtra)
			? HIDDEN_NAVBAR_HEIGHT
			: DEFAULT_NAVBAR_HEIGHT;
	}

	ngOnInit(): void {
		combineLatest([this.noFlyTimeService.noFlyTimes$, this.noFlyTimeService.noFlyTimeEnabled])
			.pipe(takeUntil(this.destroy$))
			.subscribe(([noFlyTimes, noFlyTimeEnabled]) => {
				this.noFlyTimes = noFlyTimes;
				this.currentNoFlyTimeIndex = this.noFlyTimeService.checkNoFlyTimes(this.noFlyTimes);
				if (this.currentNoFlyTimeIndex >= 0 && noFlyTimeEnabled) {
					this.noFlyTimeActive = true;
					const currentEndTime = this.noFlyTimeService.momentize(this.noFlyTimes[this.currentNoFlyTimeIndex].end_time);
					this.currentNoFlyEndTimeString = this.noFlyTimeService.formatTimeString(currentEndTime);
				} else {
					this.noFlyTimeActive = false;
				}
			});

		const queryParamsToRemove = ['bypassIntroGuards']; // Specify the query parameters to remove
		const currentQueryParams = { ...this.router.parseUrl(this.router.url).queryParams }; // Get the current query parameters
		queryParamsToRemove.forEach((param) => delete currentQueryParams[param]); // Remove the specified query parameters
		const navigationExtras: NavigationExtras = {
			queryParams: currentQueryParams,
		};

		this.router.navigate([], navigationExtras);

		this.isKioskMode = this.kioskModeService.isKioskMode();
		this.toggleLeft = this.sideNavService.toggleLeft;

		this.toggleLeft.pipe(takeUntil(this.destroy$)).subscribe((res) => {
			if (res) {
				document.documentElement.style.position = 'fixed';
			} else {
				document.documentElement.style.position = 'static';
			}
		});

		this.userService.userJSON$
			.pipe(
				filter(Boolean),
				map((user) => User.fromJSON(user)),
				take(1),
				filter((user) => !user.isTeacher() && user.isAdmin()),
				takeUntil(this.destroy$)
			)
			.subscribe(() => {
				window.waitForAppLoaded();
				this.goHome();
			});

		this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event) => {
			if (event instanceof NavigationEnd) {
				this.isKioskMode = this.kioskModeService.isKioskMode();
				this.navbarHeight = this.currentNavbarHeight();
			}
		});
		this.listenEmergency();
		this.checkForEvents();
		// this.listenEmergencyMessage();
	}

	ngAfterViewInit(): void {
		window.appLoaded();
	}

	ngOnDestroy(): void {
		this.destroy$.next(undefined);
		this.destroy$.complete();
	}

	private checkForEvents(): void {
		const body = {
			state: 'started',
		};
		this.emergencyService
			.getEmergencyEvent(body)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (events) => {
					const event = events?.[0];
					if (event?.state == 'started') {
						// this.emergencyData = event;
						this.emergencyActive = true;
						this.emergencyService.isEmergencyActivated.next(true);
						// this.getMessage();
					}
				},
				error: (error) => {
					console.log('error : ', error);
				},
			});
	}

	private listenEmergency(): void {
		this.emergencyService
			.listenForEmergencyEventsUpdate()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (result: any) => {
					if (result?.data && this.emergencyService.isRightSchool(result?.data?.school_id)) {
						if (result?.data?.state == 'ended') {
							this.emergencyActive = false;
							this.emergencyService.isEmergencyActivated.next(false);
						}
					}
				},
			});

		this.emergencyService
			.listenForEmergencyEventsCreate()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (result: any) => {
					if (result?.data[0] && this.emergencyService.isRightSchool(result?.data[0]?.school_id)) {
						if (result?.data[0]?.state == 'started') {
							// this.emergencyData = result.data;
							this.emergencyActive = true;
							this.emergencyService.isEmergencyActivated.next(true);
						}
					}
				},
			});

		this.emergencyService
			.listenForEmergencyEventsUpdatePublic()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (result: any) => {
					if (result?.data && this.emergencyService.isRightSchool(result?.data?.school_id)) {
						if (result?.data?.state == 'ended') {
							// this.emergencyActive = false;
							this.emergencyService.isEmergencyActivated.next(false);
						}
					}
				},
			});

		this.emergencyService
			.listenForEmergencyEventsCreatePublic()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (result: any) => {
					if (result?.data && this.emergencyService.isRightSchool(result?.data?.school_id)) {
						if (result?.data?.state == 'started') {
							// this.emergencyData = result.data;
							// this.emergencyActive = true;
							this.emergencyService.isEmergencyActivated.next(true);
						}
					}
				},
			});
	}

	private goHome(): void {
		const studentRedirectFromAdmin = this.storage.getItem('admin_not_teacher_student_redirect');
		if (studentRedirectFromAdmin) {
			const urlCommands = ['main', 'student', studentRedirectFromAdmin];
			this.router.navigate(urlCommands);
			this.storage.removeItem('admin_not_teacher_student_redirect');
			return;
		}

		this.router.navigate(['/admin']);
		return;
	}

	onSettingClick($event: DialogData): void {
		if (this.screenService.isDeviceLargeExtra) {
			this.data = $event;
			this.sideNavService.toggle$.next(true);
			this.sideNavService.toggleLeft$.next(true);
			this.sideNavService.sideNavData$.next($event);
		}
	}

	fadeClick(): void {
		this.sideNavService.toggleLeft$.next(false);

		this.sideNavService.sideNavAction$.next(undefined);
		this.sideNavService.fadeClick$.next(true);
	}

	openNoFlyTimeTooltip(): void {
		this.noFlyTimeTooltipDialogRef = this.dialog.open(NoFlyTimeDialogComponent, {
			data: {
				noFlyTimes: this.noFlyTimes,
				currentNoFlyTimeIndex: this.currentNoFlyTimeIndex,
				closeDialogRef: this.closeNoFlyTimeTooltip.bind(this),
			},
			panelClass: 'no-fly-time-tooltip-dialog-container',
		});
	}

	private closeNoFlyTimeTooltip(): void {
		this.noFlyTimeTooltipDialogRef?.close();
	}
}
